/**
 * "Repetir último pedido" → the items fly from the button into "No seu pedido". The reorder's
 * success launches a flight from the button; the order block, on the render where the new lines
 * appear, takes it and flies each new line in from there. A flight not taken within a few seconds
 * (the cart answered late, or nothing new came in) simply expires.
 */
interface Flight {
  readonly origin: DOMRect;
  readonly at: number;
}

const WINDOW_MS = 4000;
let pending: Flight | null = null;

export function launchFlight(origin: Element | null | undefined): void {
  if (origin == null) {
    return;
  }
  pending = { origin: origin.getBoundingClientRect(), at: performance.now() };
}

export function takeFlight(): DOMRect | null {
  const flight = pending;
  pending = null;
  return flight !== null && performance.now() - flight.at < WINDOW_MS ? flight.origin : null;
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';
const FLY_MS = 320;
const STAGGER_MS = 90;

/**
 * Flies each row's tile (`[data-flight-tile]`) from `origin` to where it sits, one after another,
 * and lets the rest of the row fade in as its tile lands. A copy of the tile does the flying, so the
 * layout never moves; the real one appears when the copy arrives.
 */
export function flyRowsIn(origin: DOMRect, rows: readonly HTMLElement[]): void {
  if (prefersReducedMotion() || typeof document.body.animate !== 'function') {
    return;
  }
  const fromX = origin.left + origin.width / 2;
  const fromY = origin.top + origin.height / 2;

  rows.forEach((row, index) => {
    const tile = row.querySelector<HTMLElement>('[data-flight-tile]');
    if (tile === null) {
      return;
    }
    const delay = index * STAGGER_MS;
    const to = tile.getBoundingClientRect();
    const copy = tile.cloneNode(true) as HTMLElement;
    Object.assign(copy.style, {
      position: 'fixed',
      left: `${String(to.left)}px`,
      top: `${String(to.top)}px`,
      width: `${String(to.width)}px`,
      height: `${String(to.height)}px`,
      margin: '0',
      zIndex: '60',
      pointerEvents: 'none',
      boxShadow: 'var(--shadow-md)',
    });
    copy.setAttribute('aria-hidden', 'true');
    document.body.appendChild(copy);

    const dx = fromX - (to.left + to.width / 2);
    const dy = fromY - (to.top + to.height / 2);
    const flight = copy.animate(
      [
        { transform: `translate(${String(dx)}px, ${String(dy)}px) scale(0.9)`, opacity: 0 },
        { opacity: 1, offset: 0.25 },
        { transform: 'none', opacity: 1 },
      ],
      { duration: FLY_MS, delay, easing: EASE_OUT, fill: 'both' },
    );
    flight.onfinish = () => {
      copy.remove();
    };
    flight.oncancel = () => {
      copy.remove();
    };

    // The real tile waits under the copy; the row's text arrives with it.
    tile.animate([{ opacity: 0 }, { opacity: 0 }], { duration: delay + FLY_MS, fill: 'backwards' });
    for (const child of row.children) {
      if (child !== tile) {
        (child as HTMLElement).animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: 200,
          delay: delay + FLY_MS * 0.6,
          easing: EASE_OUT,
          fill: 'backwards',
        });
      }
    }
  });
}
